import { useTranslation } from 'react-i18next';
import {
  JIE_QI_TO_BRANCH,
  BRANCH_FIRST_WUXING,
  JIE_QI_APPROX_DATE,
  JIE_QI_MEANING,
  JIE_QI_TIPS,
  BRANCH_TO_ZODIAC,
  getGanZhiYearExamples,
  getStemWuxing,
  getWuxingRelationDesc,
} from '../jieQiData';
import { JIE_QI_FENYE } from '@/utils/calcTools/bazi/yuelingEngine';

interface Props {
  jieQiIdx: number | null;
  dayStem: string;
  isOpen: boolean;
  onClose: () => void;
  isDark?: boolean;
}

export default function JieQiModal({ jieQiIdx, dayStem, isOpen, onClose, isDark = true }: Props) {
  const { t } = useTranslation('paipan');

  if (!isOpen || jieQiIdx === null) return null;

  const branch = JIE_QI_TO_BRANCH[jieQiIdx];
  const firstWx = BRANCH_FIRST_WUXING[branch];
  const zodiac = BRANCH_TO_ZODIAC[branch] || '';
  const ganZhiExamples = getGanZhiYearExamples(branch);
  const termName = t(`bazi.jieQi.${jieQiIdx}`) as string;
  const approxDate = JIE_QI_APPROX_DATE[jieQiIdx];
  const meaning = JIE_QI_MEANING[jieQiIdx];
  const tips = JIE_QI_TIPS[jieQiIdx];
  const relationDesc = firstWx ? getWuxingRelationDesc(dayStem, firstWx.wuxing) : '';
  const dayWuxing = getStemWuxing(dayStem);

  // 人元司令分野数据
  const fenyeItems = JIE_QI_FENYE[branch] || [];
  const totalDays = fenyeItems.reduce((s, it) => s + it.days, 0);
  const fenyeWithPercent = fenyeItems.map(it => ({
    ...it,
    percent: totalDays > 0 ? Math.round((it.days / totalDays) * 100) : 0,
  }));

  const overlayBg = isDark ? 'rgba(15,10,18,0.75)' : 'rgba(60,50,40,0.55)';
  const cardBg = isDark ? '#1A1420' : 'var(--ink-paper)';
  const cardBorder = isDark ? 'rgba(183,154,197,0.25)' : 'rgba(232,160,32,0.25)';
  const titleColor = 'var(--accent-secondary)';
  const textColor = 'var(--text-primary)';
  const labelColor = 'var(--text-secondary)';
  const sectionBg = isDark ? 'rgba(183,154,197,0.06)' : 'rgba(232,160,32,0.06)';

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-0 sm:px-4"
      style={{ background: overlayBg }}
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col max-h-[80dvh] sm:max-h-[85dvh]"
        style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
        onClick={e => e.stopPropagation()}
      >
        {/* 拖拽把手（仅移动端） */}
        <div className="flex justify-center pt-2 pb-1 sm:hidden">
          <div className="w-10 h-1 rounded-full" style={{ background: isDark ? 'rgba(183,154,197,0.3)' : 'rgba(232,160,32,0.25)' }} />
        </div>

        {/* 头部 — 固定 */}
        <div className="px-4 pt-2 sm:pt-4 pb-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
              style={{ background: firstWx?.color ? `${firstWx.color}20` : sectionBg, color: firstWx?.color || titleColor, border: `1px solid ${firstWx?.color ? `${firstWx.color}40` : cardBorder}` }}
            >
              {branch}
            </div>
            <div>
              <h3 className="text-base font-bold" style={{ color: titleColor, fontFamily: "'Noto Serif TC', serif" }}>
                {termName}
              </h3>
              <p className="text-[10px]" style={{ color: labelColor }}>
                {zodiac && `${zodiac}年 · `}{branch}月
              </p>
            </div>
          </div>
          <button
            className="w-7 h-7 rounded-full flex items-center justify-center active:opacity-70 transition-opacity"
            style={{ background: sectionBg }}
            onClick={onClose}
          >
            <i className="ri-close-line text-sm" style={{ color: labelColor }} />
          </button>
        </div>

        {/* 内容区 — 可滚动 */}
        <div className="px-4 pb-6 space-y-3 overflow-y-auto flex-1" style={{ scrollbarWidth: 'thin' }}>
          {/* 干支年份示例 */}
          <div className="rounded-xl p-2.5" style={{ background: sectionBg }}>
            <div className="flex items-center gap-1.5 mb-1.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-line text-[10px]" style={{ color: titleColor }} />
              </div>
              <span className="text-[10px] font-bold" style={{ color: titleColor }}>对应干支年</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {ganZhiExamples.map((gz, i) => (
                <span
                  key={i}
                  className="text-[10px] px-2 py-0.5 rounded-md font-medium"
                  style={{ background: isDark ? 'rgba(107,78,113,0.25)' : 'rgba(232,160,32,0.12)', color: textColor }}
                >
                  {gz}年
                </span>
              ))}
            </div>
          </div>

          {/* 公历日期 */}
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-time-line text-[10px]" style={{ color: titleColor }} />
            </div>
            <span className="text-[10px] font-bold" style={{ color: titleColor }}>公历日期</span>
            <span className="text-[11px]" style={{ color: textColor }}>{approxDate}</span>
          </div>

          {/* 人元司令分野进度条 */}
          {fenyeWithPercent.length > 0 && (
            <div className="rounded-xl p-2.5" style={{ background: sectionBg }}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-bar-chart-horizontal-line text-[10px]" style={{ color: titleColor }} />
                  </div>
                  <span className="text-[10px] font-bold" style={{ color: titleColor }}>人元司令分野</span>
                </div>
                <span className="text-[9px]" style={{ color: labelColor }}>共{totalDays}天</span>
              </div>
              <div className="flex h-3 rounded-full overflow-hidden" style={{ background: isDark ? 'rgba(107,78,113,0.3)' : 'rgba(232,160,32,0.12)' }}>
                {fenyeWithPercent.map((it, i) => (
                  <div
                    key={i}
                    className="h-full relative"
                    style={{
                      width: `${it.percent}%`,
                      background: it.color,
                      minWidth: it.days > 0 ? 2 : 0,
                    }}
                    title={`${it.stem}${it.element} ${it.days}天`}
                  >
                    {it.percent >= 15 && (
                      <span className="absolute inset-0 flex items-center justify-center text-[0.5rem] font-bold text-white/80 whitespace-nowrap">
                        {it.stem}
                      </span>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 flex-wrap mt-2">
                {fenyeWithPercent.map((it, i) => (
                  <div key={i} className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: it.color }} />
                    <span className="text-[9px]" style={{ color: textColor }}>
                      {it.stem}{it.element} {it.days}天
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 释义 */}
          <div className="rounded-xl p-2.5" style={{ background: sectionBg }}>
            <div className="flex items-center gap-1.5 mb-1">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-book-open-line text-[10px]" style={{ color: titleColor }} />
              </div>
              <span className="text-[10px] font-bold" style={{ color: titleColor }}>释义</span>
            </div>
            <p className="text-[0.65rem] leading-relaxed" style={{ color: isDark ? 'rgba(232,224,232,0.85)' : '#4A4A4A' }}>
              {meaning}
            </p>
          </div>

          {/* 生活建议 */}
          <div className="rounded-xl p-2.5" style={{ background: sectionBg }}>
            <div className="flex items-center gap-1.5 mb-1">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-lightbulb-line text-[10px]" style={{ color: titleColor }} />
              </div>
              <span className="text-[10px] font-bold" style={{ color: titleColor }}>生活建议</span>
            </div>
            <p className="text-[0.65rem] leading-relaxed" style={{ color: isDark ? 'rgba(232,224,232,0.85)' : '#4A4A4A' }}>
              {tips}
            </p>
          </div>

          {/* 当令五行 */}
          {firstWx && (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-fire-line text-[10px]" style={{ color: firstWx.color }} />
              </div>
              <span className="text-[10px] font-bold" style={{ color: firstWx.color }}>当令五行</span>
              <span className="text-[11px] font-bold" style={{ color: firstWx.color }}>
                {firstWx.stem} · {firstWx.wuxing}
              </span>
              <span
                className="text-[0.5rem] px-1.5 py-0.5 rounded-full"
                style={{ background: `${firstWx.color}20`, color: firstWx.color, border: `1px solid ${firstWx.color}40` }}
              >
                人元司令
              </span>
            </div>
          )}

          {/* 与日主生克关系 */}
          {relationDesc && (
            <div className="rounded-xl p-2.5 border" style={{ background: isDark ? `${firstWx?.color || titleColor}10` : `${firstWx?.color || titleColor}08`, borderColor: `${firstWx?.color || titleColor}30` }}>
              <div className="flex items-center gap-1.5 mb-1">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-exchange-line text-[10px]" style={{ color: firstWx?.color || titleColor }} />
                </div>
                <span className="text-[10px] font-bold" style={{ color: firstWx?.color || titleColor }}>与日主关系</span>
              </div>
              <p className="text-[0.65rem] leading-relaxed mb-1" style={{ color: isDark ? 'rgba(232,224,232,0.85)' : '#4A4A4A' }}>
                日主<strong style={{ color: firstWx?.color || titleColor }}>{dayStem}（{dayWuxing}）</strong>
                遇{branch}月（<strong style={{ color: firstWx?.color || titleColor }}>{firstWx?.wuxing}</strong>）
              </p>
              <p className="text-[0.65rem] leading-relaxed" style={{ color: textColor }}>
                {relationDesc}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}